import { useState } from "react";
import { motion } from "framer-motion";
import { Send, CheckCircle, Linkedin, Mail } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.message) {
      toast.error("Please fill in all fields");
      return;
    }

    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      setIsSubmitted(true);
      toast.success("Message sent successfully!");
      setFormData({ name: "", email: "", message: "" });
    } catch (error) {
      console.error("Error submitting contact form:", error);
      toast.error("Failed to send message. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen pt-20" data-testid="contact-page">
      {/* Header */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container-main text-center">
          <motion.div {...fadeInUp}>
            <p className="caption mb-4">Contact</p>
            <h1 className="heading-1 mb-4">Get in touch.</h1>
            <p className="body-lg max-w-xl mx-auto">
              Have a question, feedback, or just want to say hello? 
              We'd love to hear from you.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="pb-24 bg-white">
        <div className="container-main">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
            {/* Form */}
            <motion.div {...fadeInUp}>
              {isSubmitted ? (
                <div className="feature-card text-center py-16">
                  <div className="w-16 h-16 rounded-full bg-zinc-100 flex items-center justify-center mx-auto mb-6">
                    <CheckCircle className="w-8 h-8 text-zinc-600" />
                  </div>
                  <h3 className="heading-3 mb-4">Thank you!</h3>
                  <p className="body-text mb-6">
                    Your message has been sent. We'll get back to you soon.
                  </p>
                  <Button
                    onClick={() => setIsSubmitted(false)}
                    className="btn-secondary"
                    data-testid="send-another-btn"
                  >
                    Send another message
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                  <div className="space-y-2">
                    <Label htmlFor="name" className="text-sm font-medium text-zinc-900">
                      Name
                    </Label>
                    <Input
                      id="name"
                      name="name"
                      type="text"
                      placeholder="Your name"
                      value={formData.name}
                      onChange={handleChange}
                      className="h-12 rounded-xl border-zinc-200 focus:border-zinc-400 focus:ring-zinc-400"
                      data-testid="contact-name-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-sm font-medium text-zinc-900">
                      Email
                    </Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      placeholder="your@email.com"
                      value={formData.email}
                      onChange={handleChange}
                      className="h-12 rounded-xl border-zinc-200 focus:border-zinc-400 focus:ring-zinc-400"
                      data-testid="contact-email-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message" className="text-sm font-medium text-zinc-900">
                      Message
                    </Label>
                    <Textarea
                      id="message"
                      name="message"
                      placeholder="How can we help?"
                      value={formData.message}
                      onChange={handleChange}
                      rows={6}
                      className="rounded-xl border-zinc-200 focus:border-zinc-400 focus:ring-zinc-400 resize-none"
                      data-testid="contact-message-input"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="btn-primary w-full"
                    data-testid="contact-submit-btn"
                  >
                    {isSubmitting ? (
                      "Sending..."
                    ) : (
                      <>
                        Send Message
                        <Send className="w-4 h-4 ml-2" />
                      </>
                    )}
                  </Button>
                </form>
              )}
            </motion.div>

            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
              className="lg:pl-8"
            >
              <div className="space-y-8">
                <div>
                  <h3 className="heading-3 mb-4">Other ways to reach us</h3>
                  <p className="body-text">
                    Feel free to reach out through any of the channels below.
                  </p>
                </div>

                <div className="space-y-6">
                  <a
                    href="mailto:info@theappfam.com"
                    className="feature-card flex items-center gap-4 group"
                    data-testid="contact-email-link"
                  >
                    <div className="w-12 h-12 rounded-full bg-zinc-100 flex items-center justify-center text-zinc-600 group-hover:bg-zinc-200 transition-colors">
                      <Mail className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="text-sm text-zinc-400">Email</p>
                      <p className="font-medium text-zinc-900">info@theappfam.com</p>
                    </div>
                  </a>

                  <a
                    href="https://www.linkedin.com/in/aarondpenha/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="feature-card flex items-center gap-4 group"
                    data-testid="contact-linkedin-link"
                  >
                    <div className="w-12 h-12 rounded-full bg-zinc-100 flex items-center justify-center text-zinc-600 group-hover:bg-zinc-200 transition-colors">
                      <Linkedin className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="text-sm text-zinc-400">LinkedIn</p>
                      <p className="font-medium text-zinc-900">Aaron DPenha</p>
                    </div>
                  </a>
                </div>

                <div className="pt-8 border-t border-zinc-100">
                  <p className="text-sm text-zinc-400">
                    We typically respond within 24-48 hours.
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
